import { useState } from "react";
import {
    useAddNewIntegrationMutation,
    useEditIntegrationMutation,
    useGetIntegrationsQuery,
    useVrboQueryPropertyMutation,
} from "../../services/bClientApi";

// payload for a new VRBO integration record, built from a "query property" response
export function buildVrboIntegrationPayload(propertyId: string, vrboPropertyData: any) {
    const now = new Date();
    return {
        entityType: 'property',
        entityId: propertyId,
        provider: 'vrbo',
        primaryExternalId: vrboPropertyData.response.property.id,
        externalIds: {},
        overrideData: {},
        sync: {
            status: 'new',
            lastSyncedAt: now,
            lastAttemptAt: null,
            lastError: null,
        },
        rawData: vrboPropertyData.response.property,
        createdAt: now,
        updatedAt: now,
    }
}

// Loads the VRBO integration record for a property and handles refreshing it from VRBO.
export default function useVrboIntegrationRecord(propertyId: string) {
    const [vrboPropertyData, setVrboPropertyData] = useState<any>(null)
    const [vrboQueryErrorData, setVrboQueryErrorData] = useState<any>(null)

    // find integration entry for this entity (property) and provider (VRBO)
    const {
        data,
        isLoading,
        error,
    } = useGetIntegrationsQuery({
        integrationsQuery: {
            entityType: 'property',
            entityId: propertyId,
            provider: 'vrbo',
        },
        pagination: {
            page: 1,
            limit: 1,
        },
    }, {
        skip: !propertyId,
    })

    const integrationData = data?.docs?.[0] || null

    const [createIntegration, { isLoading: isCreatingIntegration, error: createIntegrationError }] = useAddNewIntegrationMutation()
    const [editIntegration, { isLoading: isUpdatingIntegration, error: updateIntegrationError }] = useEditIntegrationMutation()
    const [queryVRBOProperty, { isLoading: isQueryingVRBO }] = useVrboQueryPropertyMutation()

    const handleQueryVRBOProperty = async () => {
        if (!propertyId) return;
        try {
            const response = await queryVRBOProperty(propertyId).unwrap();
            setVrboPropertyData(response);
        } catch (error) {
            console.error('Error querying VRBO property:', error);
            setVrboPropertyData(null);
            setVrboQueryErrorData(error);
        }
    };

    // records a successful push to VRBO; edit replaces the whole sync object so keep the existing fields
    const markSynced = async () => {
        if (!integrationData?._id) return;
        const now = new Date();
        try {
            await editIntegration({
                integrationId: integrationData._id,
                integrationInfo: {
                    sync: {
                        ...integrationData.sync,
                        lastSyncedAt: now,
                    },
                    updatedAt: now,
                },
            }).unwrap();
        } catch (error) {
            console.error('Error updating integration last synced time:', error);
        }
    }

    const createIntegrationData = async () => {
        if (!propertyId || !vrboPropertyData?.response?.property) return;
        const integrationPayload = buildVrboIntegrationPayload(propertyId, vrboPropertyData)

        try {
            const response = await createIntegration(integrationPayload).unwrap();
            console.log('Integration created:', response);
            setVrboPropertyData(null);
        } catch (error) {
            console.error('Error creating integration:', error);
        }
    }

    const updateIntegrationData = async () => {
        if (!integrationData?._id || !vrboPropertyData?.response?.property) return;
        try {
            const response = await editIntegration({
                integrationId: integrationData._id,
                integrationInfo: {
                    primaryExternalId: vrboPropertyData.response.property.id,
                    rawData: vrboPropertyData.response.property,
                    updatedAt: new Date(),
                },
            }).unwrap();
            console.log('Integration updated:', response);
            setVrboPropertyData(null);
        } catch (error) {
            console.error('Error updating integration:', error);
            setVrboPropertyData(null);
        }
    }

    return {
        integrationData,
        isLoading,
        error,
        vrboPropertyData,
        setVrboPropertyData,
        vrboQueryErrorData,
        setVrboQueryErrorData,
        isQueryingVRBO,
        handleQueryVRBOProperty,
        createIntegrationData,
        isCreatingIntegration,
        createIntegrationError,
        updateIntegrationData,
        markSynced,
        isUpdatingIntegration,
        updateIntegrationError,
    }
}

export type VrboIntegrationRecord = ReturnType<typeof useVrboIntegrationRecord>
